import { Injectable } from '@angular/core';

import { ModelNameFlags } from '@era/locations-sdk';
import {
  AddAutomatedConfigurationCommand,
  ApplicationId,
  AutomatedAppLock,
  AutomatedConfiguration,
  AutomatedConfigurationService,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AddOperation, compare } from 'fast-json-patch';
import { map, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { RolloutApiActions, RolloutListActions, RolloutResolverActions } from './automated-rollouts.actions';

@Injectable()
export class AutomatedRolloutEffects {
  constructor(
    private readonly automatedConfigurationService: AutomatedConfigurationService,
    private readonly actions$: Actions
  ) {}

  rolloutsLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RolloutResolverActions.rolloutsRequested),
      mergeMap(() =>
        safeMap(
          this.automatedConfigurationService.getAutomatedConfigurations(),
          (automatedConfigurations: AutomatedConfiguration[]) =>
            RolloutApiActions.rolloutsLoaded({ automatedConfigurations }),
          (error) => RolloutApiActions.rolloutsLoadedError({ error })
        )
      )
    );
  });

  rolloutAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RolloutListActions.rolloutAdded),
      map(({ automatedConfiguration }) => {
        const applicationIds: ApplicationId[] | undefined = automatedConfiguration.applications?.map((application) => {
          return {
            bundleIdentifier: application.bundleIdentifier,
            version: application.version,
          };
        });

        const addedRollout: AddAutomatedConfigurationCommand = {
          id: automatedConfiguration.id,
          roomIds: automatedConfiguration.roomIds,
          applicationIds: applicationIds,
        };

        return addedRollout;
      }),
      mergeMap((addAutomatedConfigurationCommand) =>
        safeMap(
          this.automatedConfigurationService.addAutomatedConfiguration(addAutomatedConfigurationCommand),
          () => RolloutApiActions.rolloutAdded({ addAutomatedConfigurationCommand }),
          (error) => RolloutApiActions.rolloutAddedError({ addAutomatedConfigurationCommand, error })
        )
      )
    );
  });

  rolloutDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RolloutListActions.rolloutDeleted),
      mergeMap(({ automatedConfiguration }) =>
        safeMap(
          this.automatedConfigurationService.deleteAutomatedConfiguration(automatedConfiguration.id),
          () => RolloutApiActions.rolloutDeleted({ id: automatedConfiguration.id }),
          (error) => RolloutApiActions.rolloutDeletedError({ id: automatedConfiguration.id, error })
        )
      )
    );
  });

  rolloutUpdatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RolloutListActions.rolloutUpdated),
      mergeMap(({ originalAutomatedConfiguration, newAutomatedConfiguration }) => {
        // This mess is necessary to map an array of ModelNameFlags to a string that JsonPatchDocument will acccept.
        // It finds all add operations for appLocks, and joins the models with a commma and a space
        const update = compare(originalAutomatedConfiguration, newAutomatedConfiguration).map(
          (operation: AddOperation<unknown>) => {
            if (!operation.path.startsWith('/appLocks/') || operation.op !== 'add') return operation;
            const opVal = operation.value;

            if (typeof opVal !== 'object') return operation;
            if (!('models' in opVal)) return operation;

            const models: unknown = (opVal as Record<string, unknown>).models;
            if (typeof models !== 'object') return operation;
            if (!Array.isArray(models)) return operation;

            const joinedModels = models.join(', ');

            const newOperation: AddOperation<AutomatedAppLock> = {
              ...operation,
              value: {
                ...opVal,
                models: joinedModels as unknown as ModelNameFlags[],
              },
            };

            return newOperation;
          }
        );
        return safeMap(
          this.automatedConfigurationService.patchAutomatedConfiguration(originalAutomatedConfiguration.id, update),
          () => null,
          (error) =>
            RolloutApiActions.rolloutUpdatedError({
              id: originalAutomatedConfiguration.id,
              error,
            })
        ).pipe(
          mergeMap(() =>
            safeMap(
              this.automatedConfigurationService.getAutomatedConfiguration(originalAutomatedConfiguration.id),
              (res) => RolloutApiActions.rolloutUpdated({ newAutomatedConfiguration: res }),
              (error) =>
                RolloutApiActions.rolloutUpdatedError({
                  id: originalAutomatedConfiguration.id,
                  error,
                })
            )
          )
        );
      })
    );
  });
}
